import React, {useEffect, useMemo} from 'react';

import {List, ListItem, ListItemText, Stack, Typography
} from '@mui/material';

import {useShallow} from 'zustand/react/shallow';

import {getPlayerById} from 'canvas/Drawer/player-utils';
import {useCanvasStore} from 'canvas/zustand';
import {useTranslation} from 'hooks';
import PlayerBadge from 'layout/components/toolbar/components/PlayerBadge';
import PlayerCircle from 'layout/components/toolbar/components/PlayerCircle';
import PlayerShirt from 'layout/components/toolbar/components/PlayerShirt';
import {DraggablePlayer} from 'module/client/team/member';
import {memberInfo} from 'module/client/team/member/useInfo';
import {groupByPosition, useApiQuery} from 'module/client/team/member/useMemberApi';
import {positions} from 'module/client/team/useTeamApi';
import {useApiQuery as useClientApiQuery} from 'module/client/useClientApi';
import {useLayoutStore} from 'store';

const styles = {
    lineupListHeaderText: theme => ({

        width: 'auto',
        borderBottom: `2px solid ${theme.palette.lineup.accordionDetailsBorder}`,
        fontSize: '14px',
        color: `${theme.palette.lineup.textPrimary} !important`,
        textAlign: 'center',
        marginBottom: '8px',
        paddingBottom: '8px',
        fontWeight: 700,
    }),
    playerContainer: theme => ({
        borderBottom: `2px solid ${theme.palette.lineup.accordionDetailsBorder}`,
        paddingBottom: '8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',

        '&:last-child': {
            borderBottom: 0,
            paddingBottom: 0,
        }
    }),
    lineupListNameText: {
        paddingX: 0,
        width: '100%',
        fontSize: '12px',
        textAlign: 'center',
    },
    listHolder: {
        padding: 3,
        width: 'auto',
    },
    lineUpContainer: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: '0',
        paddingRight: '5px',
        marginBottom: 1,

        '&:last-child': {
            marginBottom: 0,

        }
    },
    lineUpScrollContainer: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: '0',
        width: '100%',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
            height: '100%',
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'grey.light',
            borderRadius: 2
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'grey.lightest',
            borderRadius: 2
        },
        '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'grey.lightest',
            borderRadius: 2
        },
    },

};

export const Player = ({type, team, player}) => {
    const canvas = useCanvasStore(state => state.canvas);
    // watch for canvas size to trigger a re-rendering, otherwise disabled state will not be updated
    useCanvasStore(useShallow(state => state.canvas?.getObjects()));
    const disabled = !!getPlayerById(canvas, player.id);
    const {data: clients} = useClientApiQuery();
    const teamClient = clients?.find(client => client.id === team.client_id);
    
    const playerType = team?.prefs?.player?.type || 'player';

    // Get default colors from client if not set in team prefs
    const getDefaultColors = () => {
        if (teamClient?.colors?.value1 && teamClient?.colors?.value2) {
            return {
                value1: teamClient.colors.value1,
                value2: teamClient.colors.value2,
                textColor: teamClient.colors.value2,
            };
        }

        return {
            value1: type === 'home' ? 'black' : 'white',
            value2: type === 'home' ? 'white' : 'black',
            textColor: type === 'home' ? 'white' : 'black',
        };
    };

    const defaultColors = getDefaultColors();

    // fallback to selected color if not set in team prefs
    const colors = {
        value1: team?.prefs?.player?.color1 || defaultColors.value1, 
        value2: team?.prefs?.player?.color2 || defaultColors.value2, 
        textColor: team?.prefs?.player?.color2 || defaultColors.textColor, 
    };

    const renderPlayerComponent = () => {
        switch (playerType) {
        case 'playerCircle':
            return <PlayerCircle number={player.number} colors={colors} disabled={disabled} />;
        case 'playerCircleImage':
            return <PlayerBadge player={player} colors={colors} disabled={disabled} />;
        case 'player':
        default:
            return <PlayerShirt number={player.number} colors={colors} disabled={disabled} />;
        }
    };

    return (
        <DraggablePlayer 
            type={playerType} 
            team={team} 
            player={player} 
            colors={colors}
            disabled={disabled} 
            isFreePlacementPlayer={false}
        >
            {renderPlayerComponent()}
        </DraggablePlayer>
    );
};

const TeamLineup = ({type, team}) => {
    const translate = useTranslation();
    const setShowLoading = useLayoutStore((state) => state.setShowLoading);
    const queryMembers = useApiQuery({teamId: team.id});
 
    const players = useMemo(() => {
        return groupByPosition(queryMembers.data);
    }, [queryMembers.data]);

    useEffect(() => {
        setShowLoading(queryMembers.isFetching);
        return () => {
            setShowLoading(false);
        };
    }, [queryMembers.isFetching, setShowLoading]);

    return <Stack direction="column" alignItems="center" sx={{...styles.lineUpContainer}}>
        <Stack direction="column" sx={{...styles.lineUpScrollContainer,}}>
            {positions.map(position => players[position].length > 0 && <List key={position} sx={styles.listHolder}>
                <ListItem>
                    <ListItemText><Typography sx={styles.lineupListHeaderText}>{translate(`team.position.${position}`)}</Typography></ListItemText>
                </ListItem>
                <Stack direction="column" alignItems="center" gap={2} >
                    {players[position].map(player => {
                        const {name} = memberInfo(player);

                        return (
                            <ListItem key={'player-listitem-'+ player.id} sx={styles.playerContainer}>
                                <Player type={type} key={player.id} team={team} player={player} />
                                <ListItemText><Typography sx={styles.lineupListNameText}>{name}</Typography></ListItemText>
                            </ListItem>
                        );})}
                </Stack>
            </List>)}
        </Stack>
    </Stack>;
};

export default TeamLineup;